import { ReactNode, useState } from "react";
import { Box, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight } from "lucide-react";
import useIsMobile from "../hooks/useIsMobile";
import { PAGE_FRAME_HEIGHT } from "common/constants";

interface SplitPanelProps {
  leftPanel: ReactNode;
  rightPanel?: ReactNode;
  rightPanelIcon?: ReactNode;
  rightPanelTitle?: string;
  defaultCollapsed?: boolean;
  leftPanelMaxWidth?: string;
}

export default function SplitPanel({
  leftPanel,
  rightPanel,
  rightPanelIcon,
  rightPanelTitle,
  defaultCollapsed = true,
  leftPanelMaxWidth = "950px",
}: SplitPanelProps) {
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [mobilePanel, setMobilePanel] = useState<"left" | "right">(
    defaultCollapsed ? "left" : "right",
  );

  const hasRightPanel = !!rightPanel;

  if (isMobile) {
    return (
      <Box
        sx={{
          height: "calc(100vh - 51px)",
          background: "#FDFAF7",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Left Panel */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "white",
            transform:
              mobilePanel === "left" ? "translateX(0)" : "translateX(-100%)",
            transition: "transform 0.3s ease-in-out",
            overflow: "auto",
          }}
        >
          {leftPanel}
        </Box>

        {/* Right Panel */}
        {hasRightPanel && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "white",
              transform:
                mobilePanel === "right" ? "translateX(0)" : "translateX(100%)",
              transition: "transform 0.3s ease-in-out",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                padding: "12px 16px",
                gap: "8px",
                borderBottom: "1px solid #EAEBEC",
                cursor: "pointer",
              }}
              onClick={() => setMobilePanel("left")}
            >
              <ChevronLeft size={20} color="#6B6E7B" />
              {rightPanelIcon && (
                <Box sx={{ color: "#6B6E7B", display: "flex" }}>
                  {rightPanelIcon}
                </Box>
              )}
              {rightPanelTitle && (
                <Typography variant="bodyHeavy" sx={{ fontSize: "14px" }}>
                  {rightPanelTitle}
                </Typography>
              )}
            </Box>
            {/* Content */}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
              }}
            >
              {rightPanel}
            </Box>
          </Box>
        )}

        {/* Tab to open right panel */}
        {hasRightPanel && mobilePanel === "left" && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              right: 0,
              transform: "translateY(-50%)",
              background: "white",
              borderRadius: "16px 0 0 16px",
              borderLeft: "1px solid #EAEBEC",
              borderTop: "1px solid #EAEBEC",
              borderBottom: "1px solid #EAEBEC",
              padding: "12px 8px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              boxShadow: "-2px 0 8px rgba(0,0,0,0.1)",
            }}
            onClick={() => setMobilePanel("right")}
          >
            <ChevronLeft size={20} color="#6B6E7B" />
            {rightPanelIcon && (
              <Box sx={{ color: "#6B6E7B", display: "flex" }}>
                {rightPanelIcon}
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  }

  // Desktop layout
  return (
    <Box
      className="two-panel-grid-container"
      display="flex"
      flexDirection="row"
      sx={{
        height: PAGE_FRAME_HEIGHT,
        background: "#FDFAF7",
        gap: "12px",
      }}
    >
      {/* Left Panel */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          borderRadius: "16px 16px 0 0",
          border: "1px solid #EAECF0",
          background: "white",
          overflow: "hidden",
          flex: 1,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            width: "100%",
            maxWidth: leftPanelMaxWidth,
            alignSelf: "center",
            overflow: "auto",
            /* Hide scrollbar for Firefox */
            scrollbarWidth: "none",
            /* Hide scrollbar for IE and Legacy Edge */
            "-ms-overflow-style": "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {leftPanel}
        </Box>
      </Box>

      {/* Right Panel */}
      {hasRightPanel && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "auto",
            alignSelf: "flex-start",
            transition: "all 0.25s ease-in-out",
            width: collapsed ? "72px" : "350px",
            overflow: "hidden",
            marginTop: collapsed ? "36px" : "0",
          }}
        >
          <Box
            sx={{
              borderLeft: "1px solid #EAEBEC",
              borderBottom: "1px solid #EAEBEC",
              borderRadius: "16px 0 0 16px",
              height: collapsed ? "auto" : `calc(${PAGE_FRAME_HEIGHT})`,
              display: "flex",
              flexDirection: "column",
              background: "white",
              width: "100%",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            {/* Header */}
            {collapsed ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  padding: "8px 12px",
                  gap: "8px",
                  cursor: "pointer",
                  height: "48px",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.02)",
                  },
                }}
                onClick={() => setCollapsed(false)}
              >
                <ChevronLeft size={20} />
                {rightPanelIcon && (
                  <Box sx={{ color: "#6B6E7B", display: "flex" }}>
                    {rightPanelIcon}
                  </Box>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  padding: "8px 12px",
                  gap: "8px",
                  cursor: "pointer",
                  borderBottom: "1px solid #EAEBEC",
                  height: "48px",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.02)",
                  },
                }}
                onClick={() => setCollapsed(true)}
              >
                <ChevronRight size={20} />
                {rightPanelIcon && (
                  <Box sx={{ color: "#6B6E7B", display: "flex" }}>
                    {rightPanelIcon}
                  </Box>
                )}
                {rightPanelTitle && (
                  <Typography variant="bodyHeavy" sx={{ fontSize: "14px" }}>
                    {rightPanelTitle}
                  </Typography>
                )}
              </Box>
            )}

            {/* Content */}
            {!collapsed && (
              <Box
                sx={{
                  flex: 1,
                  overflowX: "hidden",
                  overflowY: "auto",
                  minWidth: 0,
                  width: "100%",
                }}
              >
                {rightPanel}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
